// apps/client/src/hooks/useTrailRiskStatusForRefs.ts
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/** Mirrors TrailRiskEventType in @valutten/shared-schemas. */
export type TrailRiskEventType =
  | 'loan_discharged'
  | 'arrears_commission'
  | 'clawback'
  | 'trail_dropped_off'
  | 'trail_stopped';

/**
 * Mirrors TrailRiskRefState in
 * services/client-functions/src/clientCare/getTrailRiskStatusForRefs.ts.
 * Every one of these renders differently; none of them renders as blank.
 */
export type TrailRiskRefState =
  | 'open'
  | 'actioned'
  | 'dismissed'
  | 'before_tracking_floor'
  | 'tracking_not_enabled'
  | 'pending_materialisation';

export interface TrailRiskRef {
  loanId: string;
  eventType: TrailRiskEventType;
  /** 'YYYY-MM' — the period the ROW represents. */
  periodMonth: string;
}

export interface TrailRiskRefStatus extends TrailRiskRef {
  state: TrailRiskRefState;
  /** Null unless a real event backs this ref; required to close it. */
  eventId: string | null;
  /** Null unless a real event backs this ref; logClientContact needs it. */
  clientId: string | null;
  /** This drop-off row is owned by a discharge incident; do not offer a second form. */
  aliasOf?: 'loan_discharged';
}

/** Server cap (MAX_REFS in getTrailRiskStatusForRefs.ts). Requests above it are rejected. */
const MAX_REFS_PER_CALL = 500;

/**
 * 'YYYY-MM' for a report row's date, matching the server key EXACTLY.
 *
 * The server derives a discharge event's periodMonth as
 * `discharge_date.slice(0, 7)` on the BigQuery DATE string, so the only safe
 * client equivalent is the UTC month. `getMonth()`/`getFullYear()` are LOCAL and
 * would move a 1st-of-month date into the previous month for any viewer west of
 * UTC, producing a ref that silently matches no event.
 */
export function periodMonthFromDate(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  if (typeof d === 'string') return /^\d{4}-\d{2}/.test(d) ? d.slice(0, 7) : null;
  if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toISOString().slice(0, 7);
  return null;
}

/** Must stay identical to the server's exported refKey(). */
export const refKey = (r: { loanId: string; eventType: string; periodMonth: string }): string =>
  `${r.loanId}:${r.eventType}:${r.periodMonth}`;

/**
 * Per-OCCURRENCE trail-risk follow-up status for a report page.
 *
 * Replaces `useTrailRiskStatus`, which collapsed the server's events to one row
 * per loanId. Trail-risk events are keyed per loan AND per period, so that
 * collapse handed the WRONG period's eventId to `logClientContact`: on a loan
 * with arrears open in June and July, clicking July closed June and left July
 * looking untouched. Here the caller states the exact occurrences its rows
 * represent and gets one answer per occurrence, keyed by `refKey`.
 *
 * FAILURE IS VISIBLE, NEVER SILENT. On error the map is empty AND `error` is set,
 * because an empty map alone reads as "nothing to follow up" — the most dangerous
 * possible lie on a retention report. Pages must render "Status unavailable" when
 * `error` is set rather than hiding the chip.
 */
export function useTrailRiskStatusForRefs(companyId: string | undefined, refs: TrailRiskRef[]) {
  const [statusByRef, setStatusByRef] = useState<Map<string, TrailRiskRefStatus>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Serialise so a caller that rebuilds its ref array every render (the normal
  // case: refs are derived from rows inside JSX) does not refetch every render.
  // De-duplicated and sorted, so two identical ref SETS in different orders are
  // one cache key.
  const refsKey = useMemo(() => {
    const seen = new Set<string>();
    for (const r of refs) {
      if (!r || !r.loanId || !r.periodMonth || !r.eventType) continue;
      seen.add(refKey(r));
    }
    return [...seen].sort().join('|');
  }, [refs]);

  // The array the fetch actually sends, rebuilt only when refsKey changes, so the
  // fetch callback does not depend on the caller's unstable array identity.
  const stableRefs = useMemo<TrailRiskRef[]>(() => {
    if (!refsKey) return [];
    return refsKey.split('|').map((k) => {
      // loanId may itself contain ':' in theory, so split from the RIGHT: the last
      // two segments are always eventType and periodMonth.
      const parts = k.split(':');
      const periodMonth = parts.pop() as string;
      const eventType = parts.pop() as TrailRiskEventType;
      return { loanId: parts.join(':'), eventType, periodMonth };
    });
  }, [refsKey]);

  // Guards against a slow earlier response overwriting a newer one.
  const requestSeq = useRef(0);

  const refetch = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (!companyId || stableRefs.length === 0) {
      setStatusByRef(new Map());
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'getTrailRiskStatusForRefs');
      const batches: TrailRiskRef[][] = [];
      for (let i = 0; i < stableRefs.length; i += MAX_REFS_PER_CALL) {
        batches.push(stableRefs.slice(i, i + MAX_REFS_PER_CALL));
      }
      const results = await Promise.all(batches.map((batch) => fn({ companyId, refs: batch })));
      if (seq !== requestSeq.current) return; // superseded
      const m = new Map<string, TrailRiskRefStatus>();
      for (const res of results) {
        const items = ((res.data as { items?: TrailRiskRefStatus[] })?.items ?? []) as TrailRiskRefStatus[];
        for (const it of items) m.set(refKey(it), it);
      }
      setStatusByRef(m);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      // Rule-gated read: never silent-fail (see feedback_silent_fail_catch).
      console.error('[useTrailRiskStatusForRefs] failed', err);
      setStatusByRef(new Map());
      setError(err instanceof Error ? err.message : 'Failed to load follow-up status.');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, stableRefs]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { statusByRef, loading, error, refetch };
}
